// @flow
import { useCallback, useState, useMemo } from "react";
import { css } from "styled-components";
import { debounce } from "lodash";
import { useCombobox } from "downshift";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { ArrowContainer, Container, Input, Options, Option } from "./Select";
import { Label } from "./Label";

const clearButtonStyle = css`
  height: 100%;
  color: ${({ theme }) => theme.palette.hague};
  padding: 0 18px 0;
  opacity: 0.5;
  display: flex;
  align-items: center;
  z-index: 1;
  margin: 0;
  right: 0;
  position: absolute;
  top: 50%;
  border: none;
  background-color: transparent;
  transform: translateY(-50%);
  cursor: pointer;
`;

type SearchArea = {|
  label: string,
  value: string,
|};

type Props = {
  onChange: (area: SearchArea | null) => void,
  value: SearchArea | null,
};

export const SearchAreaSelect = ({ onChange, value: selectedItem }: Props) => {
  const [inputValue, setInputValue] = useState(selectedItem?.label || "");
  const allProperties = useSelector(
    (state) => state.forSalePage.forSaleProperties || [],
  );

  const results = useMemo(() => {
    const normalisedInputValue = inputValue.toLowerCase();
    const labelsSet = new Set();

    const addressFields = ["outcode", "borough", "street"];

    allProperties.forEach((property) => {
      addressFields.forEach((field) => {
        const fieldValue = property.address[field];
        if (fieldValue?.toLowerCase().includes(normalisedInputValue)) {
          // this automatically de-dupes the labels
          labelsSet.add(fieldValue);
        }
      });
    });

    return Array.from(labelsSet).map((label) => ({ label }));
  }, [allProperties, inputValue]);

  const debouncedOnChange = useCallback(
    debounce((state) => {
      setInputValue(state.inputValue);
    }, 500),
    [],
  );

  const {
    selectItem,
    isOpen,
    getMenuProps,
    getComboboxProps,
    getItemProps,
    getLabelProps,
    getInputProps,
    highlightedIndex,
    getToggleButtonProps,
  } = useCombobox({
    initialSelectedItem: selectedItem,
    items: results,
    itemToString: (item) => item?.label || "",
    onInputValueChange: debouncedOnChange,
    onSelectedItemChange: (changes) => {
      if (changes.selectedItem) {
        setInputValue(changes.selectedItem.label);
        onChange(changes.selectedItem);
      } else {
        setInputValue("");
        onChange(null);
      }
    },
  });

  return (
    <Container>
      <Label {...getLabelProps()}>Location</Label>
      <ArrowContainer {...getComboboxProps()}>
        <Input
          {...getToggleButtonProps()}
          {...getInputProps()}
          placeholder="Search for a location"
        />
        {selectedItem && (
          <button
            css={clearButtonStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectItem(null);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </ArrowContainer>
      {isOpen && inputValue.length > 0 && (
        <Options {...getMenuProps()}>
          {results.length === 0 && <Option>No results</Option>}
          {results.map((item, index) => (
            <Option
              {...getItemProps({
                item,
                index,
                style: {
                  backgroundColor: highlightedIndex === index ? "#FAF7F4" : "",
                  fontWeight: selectedItem === item ? "500" : "400",
                  border: highlightedIndex === index ? "solid 1px #F2EBE3" : "",
                },
              })}
            >
              <div>{item.label}</div>
            </Option>
          ))}
        </Options>
      )}
    </Container>
  );
};
